import PrizeNavigation from "../components/PrizeNavigation";
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useUserRole from '../hooks/useUserRole'; // Import useUserRole hook
import Calendar from '../components/Calendar';
import RightControls from '../components/RightControls';
import PollsTable from '../components/PollsTable';
import Button from "../components/Button";
import styles from './Polls.module.css';
import { formatInTimeZone } from 'date-fns-tz';
import { useLeague } from '../utils/LeagueContext'; // Import useLeague hook
import { useUser } from '../UserContext'; // Import useUser hook
import NoLeagueMessage from '../components/NoLeagueMessage';
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import API_BASE_URL from '../utils/apiConfig'; // Import API base URL

const Polls = () => {
  const { selectedLeague } = useLeague(); // Get selected league from context
  const { user } = useUser(); // Get user data from context
  const [pollData, setPollData] = useState([]);
  const [pollState, setPollState] = useState('noPolls');
  const [startTime, setStartTime] = useState({ hour: '', minute: '', period: 'AM' });
  const [selectedVotes, setSelectedVotes] = useState([]);
  const [pollId, setPollId] = useState(null); // New state to track pollId
  const [error, setError] = useState(null); // State for error handling
  const { leagueId: leagueIdFromURL } = useParams(); // Get leagueId from URL params
  const leagueId = selectedLeague ? selectedLeague._id : leagueIdFromURL || null; // Get leagueId from context or URL
  const userRole = useUserRole(selectedLeague); // Get user role from context
  const pollsTableRef = useRef(null); // Create a reference for the PollsTable
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [duration, setDuration] = useState(null);
  const [additionalTimes, setAdditionalTimes] = useState(0);
  const [staggerMinutes, setStaggerMinutes] = useState(0);
  const [isRightControlsValid, setIsRightControlsValid] = useState(true);  // New state for validation
  const [prevVotes, setPrevVotes] = useState([]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [formattedDate, setFormattedDate] = useState('');
  const [formattedTime, setFormattedTime] = useState('');
  const [votingDeadlineDays, setVotingDeadlineDays] = useState(7);
  const [calendarMonth, setCalendarMonth] = useState(null);
  const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL;
  useLeagueAccessGuard(); // Automatically handles auth, sets league, validates membership

  useEffect(() => {
    fetchPollDetails();
  }, []);

  const fetchPollDetails = async (pollId) => { // Get poll options based on pollId
    if (!pollId || !user.token) return;
  
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/pollsDetails/poll/${pollId}`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 404 && errorData.message === 'Poll details not found') {
          setPollData([]);
          return;
        }
        throw new Error('Failed to fetch poll details');
      }
  
      const pollDetails = await response.json();
      setPollData(Array.isArray(pollDetails) ? pollDetails : []);
    } catch (error) {
      console.error('Error fetching poll details:', error);
      setPollData([]);
    }
  };  


  const fetchPolls = async () => { // Get poll details based on selectedLeague
    if (!selectedLeague || !user.token) return;
  
    setPollState('noPolls'); // Default state
  
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/polls/filter?leagueId=${selectedLeague._id}&status=open,closed,shared`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch polls');
      }
  
      const polls = await response.json();
      if (polls.length === 0) {
        setPollState('noPolls');
        setPollId(null);
        return;
      }
  
      const currentPoll = polls[0]; // Assume the first poll is the active one
      setPollId(currentPoll._id);
      fetchPollDetails(currentPoll._id); // Fetch details separately
      setPollState(currentPoll.status);

      // Validate and combine date and time
      if (currentPoll.selectedDate && currentPoll.selectedTime) {
        const combinedDateTime = new Date(`${currentPoll.selectedDate.split('T')[0]}T${currentPoll.selectedTime.split('T')[1]}`);
  
        // Convert to user's time zone
        const localDate = formatInTimeZone(combinedDateTime, timeZone, 'MMM-dd-yyyy');
        const localStartTime = formatInTimeZone(combinedDateTime, timeZone, 'hh:mm a');
  
        // Set the formatted values in state
        setFormattedDate(localDate);
        setFormattedTime(localStartTime);
      } else {
        console.warn("Invalid or missing selectedDate or selectedTime:", currentPoll.selectedDate, currentPoll.selectedTime);
        setFormattedDate('Invalid Date');
        setFormattedTime('Invalid Time');
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
      setError(error.message);
      setPollState('noPolls');
      setPollId(null);
    }
  };

  useEffect(() => {
    if (selectedLeague && user.token) {
      fetchPolls();
    }
  }, [selectedLeague, user.token]); 
  
  const handleStartTimeChange = (hour, minute, period) => {
    setStartTime({ hour, minute, period });
  };

  const handleDurationChange = (event) => {
    const selectedValue = event.target.value;
    setDuration(selectedValue); // Update duration when user selects a value
  };

  const handleAdditionalTimesChange = (value) => {
    setAdditionalTimes(value);
  };

  const handleStaggerMinutesChange = (value) => {
    setStaggerMinutes(value);
  };

  const handleValidationChange = (isValid) => {
    setIsRightControlsValid(isValid);
  };

  const handleCreatePollClick = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/polls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          creatorId: user._id, // ID of the logged-in user
          leagueId: leagueId, // ID of the selected league
          status: 'open', // Status set to 'open'
          createdAt: new Date().toISOString(), // Current date and time
          deadline: new Date(Date.now() + votingDeadlineDays * 24 * 60 * 60 * 1000).toISOString(),
          season: new Date().getFullYear(),
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create poll');
      }

      const newPoll = await response.json();
      setPollData([]); // Clear pollData when a new poll is created
      setPollState('open');
      setPollId(newPoll._id); // Set the new pollId
    } catch (error) {
      console.error('Error creating poll:', error);
      setError(error.message);
    }
  };

  const handleSaveTimeClick = async () => {
    if (!isRightControlsValid) {
        alert('Please specify by how many minutes you want to stagger the additional draft times.');
        return;
    }

    if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
        console.error("Invalid selectedDate:", selectedDate);
        alert('Please select a valid date.');
        return;
    }

    if (!startTime.hour || !startTime.minute || isNaN(startTime.hour) || isNaN(startTime.minute)) {
        console.error("Invalid start time:", startTime);
        alert('Please enter a valid start time (hour and minute).');
        return;
    }

    if (!pollId) {
        alert("Oops! Seems like we are having a problem saving your poll. Please try again later.");
        return;
    }

    const formattedDate = selectedDate.toLocaleDateString('en-CA'); // YYYY-MM-DD format
    const newPolls = [{ date: formattedDate, startTime: `${startTime.hour}:${startTime.minute} ${startTime.period}`, votes: 0 }];
    
    let currentHour = parseInt(startTime.hour, 10);
    let currentMinute = parseInt(startTime.minute, 10);
    let isPM = startTime.period === 'PM';
    
    // Convert 12-hour format to 24-hour format
    if (isPM && currentHour !== 12) {
        currentHour += 12; // Convert PM times (except 12 PM) to 24-hour format
    } else if (!isPM && currentHour === 12) {
        currentHour = 0; // Convert 12 AM to 00 (midnight)
    }
    
    for (let i = 0; i < additionalTimes; i++) {
        currentMinute += staggerMinutes;
        while (currentMinute >= 60) {
            currentHour += 1;
            currentMinute -= 60;
        }
    
        // Convert back to 12-hour format for display
        let displayHour = currentHour % 12 || 12; // Convert 0 to 12 for AM cases
        let displayPeriod = currentHour >= 12 ? 'PM' : 'AM';
    
        newPolls.push({ 
            date: formattedDate, 
            startTime: `${displayHour}:${currentMinute.toString().padStart(2, '0')} ${displayPeriod}`, 
            votes: 0 
        });
    }    

    // Convert user-entered times to ISO format before checking for duplicates
    const formattedNewPolls = newPolls.map(poll => {
      const [hour, minute] = poll.startTime.split(/:| /);
      const period = poll.startTime.split(" ")[1];

      let parsedHour = parseInt(hour, 10);
      if (period === 'PM' && parsedHour !== 12) parsedHour += 12;
      if (period === 'AM' && parsedHour === 12) parsedHour = 0;

      // Construct a Date object in the user's local time
      const localDateTime = new Date(`${poll.date}T${parsedHour.toString().padStart(2, '0')}:${minute}:00`);

      // Convert to UTC string
      const utcDateTime = localDateTime.toISOString();
      return `${poll.date}T00:00:00.000Z-${utcDateTime}`;
    });

    // Check for duplicates
    const existingPollTimes = pollData.map(poll => {
      const formattedDate = poll.date.split("T")[0];  // Remove extra time if present
      return `${formattedDate}T00:00:00.000Z-${poll.startTime}`;
    });

    // check for duplicates
    const duplicatePolls = formattedNewPolls.filter(pollTime => existingPollTimes.includes(pollTime));
    
    if (duplicatePolls.length > 0) {
      alert(`Some of the selected times were previously added. Please remove duplicates and try again.`);
        return;
    }

    try {
        // Save each poll entry sequentially
        for (const poll of newPolls) {
            // Convert the poll's date and startTime to a proper Date object
            const [hour, minute] = poll.startTime.split(/:| /);
            const period = poll.startTime.split(" ")[1];

            let parsedHour = parseInt(hour, 10);
            if (period === 'PM' && parsedHour !== 12) parsedHour += 12;
            if (period === 'AM' && parsedHour === 12) parsedHour = 0;

            // const localDateTime = new Date(`${poll.date}T${parsedHour.toString().padStart(2, '0')}:${minute}:00`);
            const utcDateTime = new Date(`${poll.date}T${parsedHour.toString().padStart(2, '0')}:${minute}:00`).toISOString();

      const response = await fetch(`${API_BASE_URL}/api/pollsDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ pollId, leagueId: selectedLeague._id, date: poll.date, startTime: utcDateTime })
      });

      if (!response.ok) {
        throw new Error('Failed to save poll time');
      }
    }

    fetchPollDetails(pollId); // Fetch poll details after saving times
    alert(`Poll options successfully saved.`);
  } catch (error) {
    console.error('Error saving poll times:', error);
  }
};

// New function to handle commissioner deleting a poll option
const handleDeleteOption = async (pollOptionId) => {
  if (!window.confirm("Are you sure you want to delete this poll option?")) return;

  try {
    const response = await fetch(`${API_BASE_URL}/api/pollsDetails/${pollOptionId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${user.token}`
      }
    });

    if (!response.ok) {
      throw new Error("Failed to delete poll option.");
    }

    alert("Poll option deleted successfully.");

    // Fetch fresh poll data to update UI
    const updatedResponse = await fetch(`${API_BASE_URL}/api/pollsDetails/poll/${pollId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${user.token}`
      }
    });

    if (!updatedResponse.ok) {
      throw new Error("Failed to fetch updated poll data.");
    }

    const updatedData = await updatedResponse.json();
    setPollData(updatedData); // Update state to refresh the table

  } catch (error) {
    console.error("Error deleting poll option:", error);
    alert("Failed to delete the poll option. Please try again.");
  }
};

  const handleDateSelect = (date) => {
    setSelectedDate(new Date(date));
  };

  const handleVote = (pollOptionId) => {
    setSelectedVotes((prevVotes) => {
      // Toggle the vote selection
      if (prevVotes.includes(pollOptionId)) {
        return prevVotes.filter(id => id !== pollOptionId); // Deselect if already selected
      } else {
        return [...prevVotes, pollOptionId]; // Select if not already selected
      }
    });
  };

  // Intiialize previousVotes state to the result of the fetch call for pollDetails
  useEffect(() => {
    if (pollData.length > 0) {
      const fetchedVotes = pollData
        .filter(poll => poll.votes.includes(user._id)) // Find options the user voted for
        .map(poll => poll._id); // Extract poll IDs
  
      setPrevVotes(fetchedVotes); // Store fetched votes in state
    }
  }, [pollData, user._id]); // Runs when poll data or user changes
  
  // Save votes when the user clicks the "Submit Votes" button
  const handleSubmitVotes = async () => {
    try {
      const userId = user._id; // Logged-in user's ID
      
      // If not poll options are selected and no previous votes exist, then exit without making any API calls
      if (!selectedVotes.length && !prevVotes.length) { 
        console.warn("No votes selected or previously voted.");
        alert("Select one or more poll options to cast your vote.");
        return;
      }
  
      // Determine which votes to remove (previously selected but not currently selected)
      const votesToRemove = prevVotes.filter(voteId => !selectedVotes.includes(voteId));
  
      // Determine which votes to add (newly selected and not previously voted on)
      const votesToAdd = selectedVotes.filter(voteId => !prevVotes.includes(voteId));

      // Let user know if they didn't select any new options or remove any previous votes
      if (votesToAdd.length === 0 && votesToRemove.length === 0) {
        alert("We didn't recognize any changes to your votes. If you want to make a change to your votes, please select a poll option or remove a previous selection.");
        return;
      }
  
      // Save the selected options to the backend
      const updatePromises = [
        ...votesToAdd.map(async (pollDetailId) => {
          const response = await fetch(`${API_BASE_URL}/api/pollsDetails/${pollDetailId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${user.token}`
            },
            body: JSON.stringify({ userId, removeVote: false })
          });
  
          if (!response.ok) {
            throw new Error(`Failed to add vote for poll detail: ${pollDetailId}`);
          }
  
          return response.json();
        }),

        // Save the removed votes for the poll options that were previously selected
        ...votesToRemove.map(async (pollDetailId) => {
          const response = await fetch(`${API_BASE_URL}/api/pollsDetails/${pollDetailId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${user.token}`
            },
            body: JSON.stringify({ userId, removeVote: true }) // Remove vote
          });
  
          if (!response.ok) {
            throw new Error(`Failed to remove vote for poll detail: ${pollDetailId}`);
          }
  
          return response.json();
        })
      ];
  
      await Promise.all(updatePromises);
  
      // Fetch fresh poll data to update UI without requiring a page refresh
      const updatedResponse = await fetch(`${API_BASE_URL}/api/pollsDetails/poll/${pollId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
       }
      });

      if (!updatedResponse.ok) {
        throw new Error("Failed to fetch updated poll data.");
      }

      const updatedPolls = await updatedResponse.json();

      // Update poll data with fresh backend data
      setPollData(updatedPolls);

      // Ensure prevVotes reflects the latest votes
      setPrevVotes(selectedVotes);

      // Conditional success messages if user only removed votes
      if (votesToRemove.length > 0 && votesToAdd.length === 0) {
          alert(`You removed ${votesToRemove.length} vote(s) successfully.`);
      } else {
          alert("Votes successfully submitted!");
      }
  
    } catch (error) {
      console.error("Error submitting votes:", error);
      alert("Failed to submit votes. Please try again.");
    }
  };
  
  // Set selectedVotes based on user's past votes
  useEffect(() => {
    if (!pollData || pollData.length === 0 || !user) return;
  
    const userVotes = pollData
      .filter((pollDetail) => {
        return pollDetail.votes?.some((vote) => String(vote.userId?._id) === String(user._id));
      })
      .map((pollDetail) => pollDetail._id);
  
    setSelectedVotes(userVotes); // Track currently selected votes
    setPrevVotes(userVotes); // Track previously stored votes
  }, [pollData, user]); // Ensure it updates only when necessary
  
  const handleVoteChange = (pollId) => {
    setSelectedVotes((prevVotes) => {
      const updatedVotes = prevVotes.includes(pollId)
        ? prevVotes.filter((id) => id !== pollId) // Remove vote
        : [...prevVotes, pollId]; // Add vote

      return updatedVotes;
    });
  };

  const getSuggestedDraftDates = () => {
    const year = new Date().getFullYear();
    // Calculate NFL season start date: Thursday after Labor Day
    // Labor Day = first Monday of September
    const sept1 = new Date(year, 8, 1);
    const dayOfWeek = sept1.getDay();
    const daysUntilMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
    const laborDay = new Date(year, 8, 1 + daysUntilMonday);
    const seasonStart = new Date(laborDay);
    seasonStart.setDate(laborDay.getDate() + 3);

    // Generate 14 days before season start
    const suggestions = [];
    for (let i = 14; i >= 1; i--) {
      const date = new Date(seasonStart);
      date.setDate(seasonStart.getDate() - i);
      suggestions.push(date);
    }
    return suggestions;
  };

  // New function to handle 'Send poll' click and change state to 'shared'
  const handleSendPollClick = async () => {
    if (pollData.length === 0) {
      alert("You need to add at least one poll option before sending the poll.");
      return;
    }
    
    try { // Step 1: Update poll status to 'shared'
      const pollResponse = await fetch(`${API_BASE_URL}/api/polls/${pollId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          status: 'shared',
          selectedDate: null,
          selectedTime: null,
          deadline: new Date(Date.now() + votingDeadlineDays * 24 * 60 * 60 * 1000).toISOString(),
        })
      });

      if (!pollResponse.ok) {
        throw new Error('Failed to update poll status');
      }

      const updatedPoll = await pollResponse.json();
      setPollState('shared');

      // Step 2: Send email to each member
      if (!selectedLeague || !selectedLeague.members) {
        setError('Unable to send emails — please refresh the page and try again.');
        return;
      }

      const emailResponses = await Promise.all(
        selectedLeague.members.map(async (member) => {
          const name = member.name || member.firstName || "League Member";
          const email = member.email;

          if (!email) {
            console.warn(`Skipping member with missing email:`, member);
            return null;
          }

          const response = await fetch(`${API_BASE_URL}/api/email/sendpoll`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.token}`
            },
            body: JSON.stringify({
              to: email,
              name,
              league_name: selectedLeague.leagueName,
              action_url: `${FRONTEND_BASE_URL}/polls/${selectedLeague._id}`,
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Failed to send poll email to ${email}:`, errorText);
          }

          return response;
        })
      );

      const failures = emailResponses.filter(r => r && !r.ok).length;
      const total = emailResponses.filter(r => r !== null).length;

      if (failures > 0) {
        setError(`Poll sent but ${failures} of ${total} emails failed to send. Please try again or contact support.`);
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error sending poll emails:', error);
      setError(error.message);
    }
  };

const handleClosePollClick = async () => {
  try {
    // Fetch PollsDetails for the current pollId
    const response = await fetch(`${API_BASE_URL}/api/pollsDetails/poll/${pollId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch poll details');
    }

    const pollsDetails = await response.json();

    // Find the PollsDetails records with the highest voteCount
    const maxVoteCount = Math.max(...pollsDetails.map(option => option.voteCount));
    const tiedOptions = pollsDetails.filter(option => option.voteCount === maxVoteCount);

    // Handle ties by randomly selecting from the tied options
    const highestVotedOption = tiedOptions[Math.floor(Math.random() * tiedOptions.length)];

    // Combine date and time into a single ISO string
    const combinedDateTime = new Date(`${highestVotedOption.date.split('T')[0]}T${highestVotedOption.startTime.split('T')[1]}`);

    // Convert to user's time zone
    const localDate = formatInTimeZone(combinedDateTime, timeZone, 'MMM-dd-yyyy');
    const localStartTime = formatInTimeZone(combinedDateTime, timeZone, 'hh:mm a');

    // Update the poll with the selected date and time and set status to 'closed'
    const updateResponse = await fetch(`${API_BASE_URL}/api/polls/${pollId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      },
      body: JSON.stringify({
        status: 'closed',
        selectedDate: highestVotedOption.date,
        selectedTime: highestVotedOption.startTime
      })
    });

    if (!updateResponse.ok) {
      throw new Error('Failed to update poll status with selected date and time');
    }

    const updatedPoll = await updateResponse.json();

    setPollState('closed');
    setFormattedDate(localDate);
    setFormattedTime(localStartTime);
    alert(`Poll closed successfully! The selected draft date is ${localDate} at ${localStartTime}.`);

    // Step 3: Send poll closed emails to league members
    const fixedTimezone = 'America/New_York';
    const fixedDate = formatInTimeZone(combinedDateTime, fixedTimezone, 'MMM-dd-yyyy');
    const fixedTime = formatInTimeZone(combinedDateTime, fixedTimezone, 'hh:mm a zzz');

    const emailRequests = selectedLeague.members.map(member => {
      const emailPayload = {
        to: member.email,
        name: member.name,
        league_name: selectedLeague.leagueName,
        action_url: `${FRONTEND_BASE_URL}/polls/${selectedLeague._id}`,
        draft_date: fixedDate,
        draft_time: fixedTime,
      };

      return fetch(`${API_BASE_URL}/api/email/closepoll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(emailPayload)
      }).then(res =>
        res.json().then(data => ({
          email: member.email,
          response: data
        }))
      );
    });

      const results = await Promise.all(emailRequests);
      results.forEach(({ email, response }) => {
    });

  } catch (error) {
    console.error('Error closing poll:', error);
    alert('Failed to close the poll. Please try again.');
    setError(error.message);
  }
};

  const handleArchivePollClick = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/polls/${pollId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          status: 'archived'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update poll status');
      }

      const updatedPoll = await response.json();
      alert("Poll archived successfully. You can create a new poll for the league whenever you are ready.");
      setPollState('noPolls');
      setPollId(null);
    } catch (error) {
      console.error('Error updating poll status:', error);
      setError(error.message);
    }
  };

  // Disable the dropdown if there is data in the table
  const disableDurationDropdown = pollData.length > 0;
  
  return (
    <div className={styles.pollsPage}>
      <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
      <div className={styles.pollsContainer}>
        <>
          {error && (
            <div className={styles.errorMessage}>
              <p>Error: {error}</p>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}
          <>
            {/* No Polls State */}
            {pollState === 'noPolls' ? (
              <>
                {selectedLeague?._id ? (
                  <p className={styles.leagueName}>{selectedLeague.leagueName}</p>
                ) : (
                  <NoLeagueMessage entity="polls" className={styles.leagueName} />
                )}
                <h3 className={styles.draftDayPoll}>Draft day poll</h3>
                {userRole === 'commissioner' ? (
                  <>
                    <div className={styles.introContent}>
                      Time to choose a draft date? Create a poll, invite your league members, and choose a time that works best.
                    </div>
                    <div className={styles.createPoll}>
                      <Button label={"Create poll"} onClick={handleCreatePollClick} />
                    </div>
                  </>
                ) : (
                  <div className={styles.introContent}>
                    There are no polls right now. Sit tight and wait for your commissioner to create one.
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Open State - Member View (Same as noPolls) */}
                {pollState === 'open' && userRole === 'member' ? (
                  <>
                  {selectedLeague?._id ? (
                    <p className={styles.leagueName}>{selectedLeague.leagueName}</p>
                  ) : (
                    <NoLeagueMessage entity="polls" className={styles.leagueName}/>
                  )}
                    <h3 className={styles.draftDayPoll}>Draft day poll</h3>
                    <div className={styles.introContent}>
                      There are no polls right now. Sit tight and wait for your commissioner to create one.
                    </div>
                  </>
                ) : (
                  /* Shared UI for Open, Shared, and Closed States */
                  <div className={styles.sectionsContainer}>
                    <div className={styles.leftSection}>
                    {selectedLeague?._id ? (
                    <p className={styles.leagueName}>{selectedLeague.leagueName}</p>
                  ) : (
                    <NoLeagueMessage entity="polls" className={styles.leagueName}/>
                  )}
                      <h3 className={styles.draftDayPoll}>Draft day poll</h3>
                      {/* Shared State - Show Poll Open Message */}
                      {pollState === 'shared' ? (
                        <div className={styles.sharedContent}>
                          <p className="pollSharedText">Your poll is open for voting!</p>
                        </div>
                      ) : pollState === 'closed' ? (
                        <div className={styles.closedContent}>
                          <p className="pollClosedText">The poll is now closed. Your draft date is on <em>{formattedDate}</em> at <em>{formattedTime}</em></p>
                          <p className="pollClosedText">If there was a tie, the system randomly selected from the options with the most votes.</p>
                        </div>
                      ) : (
                        <>
                          {/* Calendar & Controls - Only for Commissioner */}
                          {pollState === 'open' && userRole === 'commissioner' && (
                            <>
                              {pollData.length === 0 && selectedLeague?.sport === 'Football' && (
                                <div className={styles.suggestedDates}>
                                  <p className={styles.suggestedDatesLabel}>Suggested draft dates for {new Date().getFullYear()}:</p>
                                  <div className={styles.suggestedDatesChips}>
                                    {getSuggestedDraftDates().map((date, index) => {
                                      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                      const label = `${dayNames[date.getDay()]} ${monthNames[date.getMonth()]} ${date.getDate()}`;
                                      return (
                                        <button
                                          key={index}
                                          className={styles.suggestedDateChip}
                                          onClick={() => {
                                            handleDateSelect(date);
                                            setCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                                          }}
                                        >
                                          {label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className={styles.deadlineSelector}>
                                <label className={styles.deadlineLabel}>
                                  How long should voting be open?
                                </label>
                                <span className={styles.deadlineSubLabel}>(days from today)</span>
                                <select
                                  className={styles.deadlineDropdown}
                                  value={votingDeadlineDays}
                                  onChange={(e) => setVotingDeadlineDays(Number(e.target.value))}
                                >
                                  <option value={3}>3</option>
                                  <option value={5}>5</option>
                                  <option value={7}>7</option>
                                  <option value={10}>10</option>
                                  <option value={14}>14</option>
                                </select>
                              </div>
                              <div className={styles.calendarAndControls}>
                                <Calendar onDateSelect={handleDateSelect} selectedDate={selectedDate} initialMonth={calendarMonth} />
                              </div>
                              <div className={styles.pollTiming}>
                                <RightControls
                                  onStartTimeChange={handleStartTimeChange}
                                  onAdditionalTimesChange={handleAdditionalTimesChange}
                                  onStaggerMinutesChange={handleStaggerMinutesChange}
                                  onDurationChange={handleDurationChange}
                                  disableDurationDropdown={disableDurationDropdown}
                                  selectedDuration={duration}
                                  onValidationChange={handleValidationChange}
                                />
                              </div>
                              <div className={styles.saveTime}>
                                <Button label={"Save"} className={styles.saveTimeButton} onClick={handleSaveTimeClick} />
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
  
                    <div className={styles.rightSection}>
                      <div className={styles.pollsSection} ref={pollsTableRef} tabIndex={-1}>
                        {/* Always show PollsTable for commissioner, even if pollData is empty */}
                        {(pollState === 'open' && userRole === 'commissioner') || pollData.length > 0 ? (
                          <>
                            {pollState === 'open' && userRole === 'commissioner' && (
                              <div className={styles.creatingPollText}>
                                <p>Add additional dates and times. When you are done, click Send poll to share the poll with your league to collect votes.</p>
                              </div>
                            )}
                            <PollsTable
                              data={pollData}
                              onSendPollClick={handleSendPollClick}
                              onClosePollClick={handleClosePollClick}
                              onArchivePollClick={handleArchivePollClick}
                              handleVote={handleVote} // Pass the handleVote function
                              selectedVotes={selectedVotes} // Pass the selectedVotes state
                              handleSubmitVotes={handleSubmitVotes} // Pass the handleSubmitVotes function
                              handleDeleteOption={handleDeleteOption} // Pass delete function
                              pollState={pollState}
                              userRole={userRole}
                              setSelectedVotes={setSelectedVotes}
                              handleVoteChange={handleVoteChange}
                            />
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        </>
      </div>
    </div>
  );
}


export default Polls;